import { formatCountries, formatAccountDetails } from '@app/helpers/accountHelpers';
import Request from './request';
import { getHeaders } from './requestHelpers';

const Paths = {
  CREATE_ACCOUNT: 'createAccount',
  USER_SIGN_UP_CONFIRM: 'activateAccount',
  ACCOUNT_DETAILS: 'accountDetails',
  ACCOUNT_DEVICES: 'casAvailableDevice',
  ACCOUNT_DEVICE_REMOVE: 'casRemoveDevice',
  LIST_COUNTRIES: 'listCountries',
  UPDATE_PERSONAL_DETAILS: 'changeDetails',
  UPDATE_ADDRESS_DETAILS: 'changeAddress',
  INVOICES_LIST: 'listInvoices',
};

/**
 * @param {Object} params
 * @param {String} params.firstName
 * @param {String} params.lastName
 * @param {String} params.addressCountry
 * @param {String} params.phoneNumber
 * @param {String} params.username
 * @param {String} params.email - user email
 * @param {String} params.emailConfirm - user emailConfirm
 * @param {String} params.password - user password
 * @param {String} params.passwordConfirm - user passwordConfirm
 * @param {String} params.languageId - user language Id
 * @param {Boolean} params.acceptTerms - user accept Terms and Conditions
 * @param {Boolean} params.allowNews - user allow send News
 * @param {String} params.partner - user allow partner
 * @param {String} params.brandId - used to to differ CONNECT accounts from BARAEM
 * @return {Promise}
 */
export async function signUp(params) {
  const { status, result, error } = await Request({
    method: 'POST',
    data: params,
    path: Paths.CREATE_ACCOUNT,
  });

  return status ? Promise.resolve(result) : Promise.reject(error);
}

/**
 * @param {Object} params
 * @param {String} params.email - user email
 * @param {String} params.idHash - user idHash
 * @param {String} params.token - user token
 * @return {Promise}
 */
export async function activateAccount(params) {
  const { status, result, error } = await Request({
    method: 'POST',
    data: params,
    path: Paths.USER_SIGN_UP_CONFIRM,
  });

  return status ? Promise.resolve(result) : Promise.reject(error);
}

/**
 * Get user account details
 * @param {String} authToken -  user security token
 * @return {Promise}
 */
export async function getAccountDetails(authToken) {
  const { status, result = {}, error } = await Request({
    method: 'POST',
    headers: getHeaders(authToken),
    path: Paths.ACCOUNT_DETAILS,
  });

  return status ? Promise.resolve(formatAccountDetails(result || {})) : Promise.reject(error);
}

/**
 * Returns a list of user devices
 * @param {Object} params
 * @param {String} params.authToken - user security token
 * @return {Promise}
 */
export async function fetchUserDevices({ authToken }) {
  const { status, result, error } = await Request({
    method: 'POST',
    headers: getHeaders(authToken),
    path: Paths.ACCOUNT_DEVICES,
  });
  return status ? Promise.resolve(result.device) : Promise.reject(error);
}

/**
 * Get remove account device
 * @param {Object} params
 * @param {String} params.casDeviceId - id of device
 * @param {String} authToken -  user security token
 * @return {Promise}
 */
export async function removeAccountDevice(params, authToken) {
  const { status, result, error } = await Request({
    method: 'POST',
    data: params,
    headers: getHeaders(authToken),
    path: Paths.ACCOUNT_DEVICE_REMOVE,
  });
  return status ? Promise.resolve(result) : Promise.reject(error);
}

/**
 * Get all countries
 * @param {Object} params
 * @param {String} params.languageId - user language Id
 * @return {Promise}
 */
export async function getCountries(params) {
  const { status, result, error } = await Request({
    method: 'POST',
    data: params,
    path: Paths.LIST_COUNTRIES,
  });

  return status ? Promise.resolve(formatCountries(result.country)) : Promise.reject(error);
}

/**
 * Update account Personal details
 * @param {Object} params
 * @param {String} params.firstName
 * @param {String} params.lastName
 * @param {String} params.email
 * @param {String} params.emailConfirm
 * @param {String} params.languageId
 * @param {Boolean} params.allowNews - user allow send News
 * @param {String} params.partner - user allow partner
 * @param {String} params.phoneNumber
 * @param {String} authToken
 * @return {Promise}
 */
export async function updateAccountPersonalDetails(params, authToken) {
  const { status, result, error } = await Request({
    method: 'POST',
    data: params,
    headers: getHeaders(authToken),
    path: Paths.UPDATE_PERSONAL_DETAILS,
  });
  return status ? Promise.resolve(result) : Promise.reject(error);
}

/**
 * Update account Address details
 * @param {Object} params
 * @param {String} params.addressCity
 * @param {String} params.addressCountry
 * @param {String} params.addressStreet
 * @param {String} params.addressZip
 * @param {String} authToken - user security token
 * @return {Promise}
 */
export async function updateAccountAddressDetails(params, authToken) {
  const { status, result, error } = await Request({
    method: 'POST',
    data: params,
    headers: getHeaders(authToken),
    path: Paths.UPDATE_ADDRESS_DETAILS,
  });
  return status ? Promise.resolve(result) : Promise.reject(error);
}

/**
 * Ask payment information
 * @param {Object} params
 * @param {Number} params.limit
 * @param {Number} params.offset
 * @param {Number} params.from
 * @param {Number} params.to
 * @param {string} params.languageId
 * @param {String} authToken -  user security token
 * @return {Promise}
 */
export async function invoicesList(params, authToken) {
  const { status, result, error } = await Request({
    method: 'POST',
    headers: getHeaders(authToken),
    data: params,
    path: Paths.INVOICES_LIST,
  });
  return status ? Promise.resolve(result.invoices) : Promise.reject(error);
}
